import React, { FC, useContext } from 'react';
import { Link } from 'react-router-dom';
import { media } from '@minecraft.themes';
import styled from 'styled-components';
import _isEmpty from 'lodash/isEmpty';
import _kebabCase from 'lodash/kebabCase';
import Button from '@minecraft.button';
import Icon from '@minecraft.icon';
import Row from '@minecraft.row';
import Col from '@minecraft.col';
import { SCREEN_NAMES, arrays } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';

import { AnchorButton, UserInfoContainer, InfoHeading, WithIsPreview } from '../styles/ProfileView';
import ProfileViewContext from '../ProfileViewContext';

const DuoLink: React.FunctionComponent<{ href: string }> = ({ href, children, ...restProps }) => (
  <Link to={href} {...restProps}>
    {children}
  </Link>
);

const { getShoeSize, getSize } = arrays;

const sectionName = 'sizes';

const UserInfoBtnContainer = styled.div`
  background: ${(props) => props.theme.colors.accent.greyBlue2};
  padding: 1rem 1rem 1rem 3rem;
  display: flex;
  justify-content: center;
  align-items: baseline;
  margin: 0 1rem;
  ${media.sm`
    margin: 0;
    justify-content: flex-start;
  `}
`;

const UserInfoSection = styled.div`
  background: ${(props) => props.theme.colors.primary.white};
  padding: 1rem;
  ${media.sm`
    padding: 1rem 3rem;
  `}
`;

const UserInfoHeading = styled.h3<WithIsPreview>`
  font-size: 1.7rem;
  line-height: 2rem;
  margin-bottom: ${(props) => (props.isPreview ? '0' : '1.5rem')};
  ${media.sm`
    margin-bottom: 1.5rem;
  `}
`;

const BorderedCol = styled(Col)`
  border-right: 0;
  ${media.sm`
    border-right: 0.0625rem solid ${(props) => props.theme.colors.primary.grey2};
  `}
`;

const SizeLabel = styled.label`
  font-size: 0.9rem;
  font-weight: bold;
`;

const SizeLabelValue = styled.label`
  font-size: 0.9rem;
`;

const SizesContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SizesSubTitle = styled.h4`
  display: flex;
  font-weight: bold;
  text-transform: uppercase;
  font-size: 1rem;
  margin-top: 1rem;
  ${media.sm`
    font-size: 0.9rem;
    margin-top: 0;
  `}
`;

const UserInfoHeadingContainer = styled.div<WithIsPreview>`
  display: flex;
  justify-content: space-between;
  @media screen and (max-width: 767px) {
    border-bottom: ${(props) => (props.isPreview ? `0.0625rem solid ${props.theme.colors.primary.ltGrey1}` : '0')};
    margin-bottom: ${(props) => (props.isPreview ? '1.5rem' : '0')};
    padding-bottom: ${(props) => (props.isPreview ? '1rem' : '0')};
    align-items: ${(props) => (props.isPreview ? 'center' : 'flex-start')};
  }
`;

const StyledIcon = styled(Icon)`
  width: 1.0625rem;
  visibility: visible;
  ${media.sm`
    visibility: hidden;
  `}
`;

const Sizes: FC = () => {
  const { t } = useTranslation();

  const {
    fetcher: { profile, countryCode, isUSCountry },
    isPreview,
    isAdmin,
    onEditClick,
  } = useContext(ProfileViewContext);

  const [state, setState] = React.useState({ openSection: true });

  const areWomenSizesAvailable = () => {
    return profile.dressSize || profile.bust || profile.underbust || profile.cupSize || profile.hipSize;
  };

  const areCommonSizesAvailable = () => {
    return (
      !_isEmpty(profile.profileTshirtSizes) ||
      !_isEmpty(profile.profileShoeSizes) ||
      !_isEmpty(profile.profilePants) ||
      profile.waist ||
      profile.inseam ||
      profile.gloveSize ||
      profile.hatSize
    );
  };

  const areMenSizesAvailable = () => {
    return profile.chestSize || profile.sleeve || profile.neck || profile.coat;
  };

  const areSizesAvailable =
    areCommonSizesAvailable() || areWomenSizesAvailable() || areMenSizesAvailable() || !_isEmpty(profile.profileSizes);

  const waistLabel = isUSCountry ? t('common:profile.sizes.waist') : t('common:profile.sizes.waistMetric');
  const inseamLabel = isUSCountry ? t('common:profile.sizes.inseam') : t('common:profile.sizes.inseamMetric');
  const { openSection } = state;
  const availableSizesButton = isAdmin ? (
    <AnchorButton
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-edit`}
      onClick={() => {
        onEditClick('sizes');
      }}
    >
      {t('common:button.edit')}
    </AnchorButton>
  ) : (
    <DuoLink
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-edit`}
      href={`/profile/${profile.profileId}/sizes`}
    >
      {t('common:button.edit')}
    </DuoLink>
  );
  const commonSizesButton = isAdmin ? (
    <AnchorButton
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-add`}
      onClick={() => {
        onEditClick('sizes');
      }}
    >
      <Button outlined="true">{t('common:profile.sizes.addSizes')} </Button>
    </AnchorButton>
  ) : (
    <DuoLink
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-add`}
      href={`/profile/${profile.profileId}/sizes`}
    >
      <Button outlined="true">{t('common:profile.sizes.addSizes')} </Button>
    </DuoLink>
  );
  const availableSizesIcon = openSection === true ? 'chevron-open-thick' : 'chevron-closed-thick';
  const waist = isUSCountry ? profile?.waist?.inchRounded : profile?.waist?.cm;
  const inseam = isUSCountry ? profile?.inseam?.inchRounded : profile?.inseam?.cm;
  const gloveSize = isUSCountry ? profile?.gloveSize?.inchRounded : profile?.gloveSize?.cm;
  const hatSize = isUSCountry ? profile?.hatSize?.inch : profile?.hatSize?.cm;
  const bust = isUSCountry ? profile?.bust?.inchRounded : profile?.bust?.cm;
  const underbust = isUSCountry ? profile?.underbust?.inchRounded : profile?.underbust?.cm;
  const hipSize = isUSCountry ? profile?.hipSize?.inchRounded : profile?.hipSize?.cm;
  const chestSize = isUSCountry ? profile?.chestSize?.inchRounded : profile?.chestSize?.cm;
  const neck = isUSCountry ? profile?.neck?.inchRounded : profile?.neck?.cm;
  const sleeve = isUSCountry ? profile?.sleeve?.inchRounded : profile?.sleeve?.cm;

  return (
    <UserInfoContainer isPreview={isPreview} id="div-sizes" data-testid="profile-sizes-container">
      {areSizesAvailable ? (
        <UserInfoSection>
          <UserInfoHeadingContainer isPreview={isPreview}>
            <UserInfoHeading isPreview={isPreview}>{t('common:profile.sizes.title')}</UserInfoHeading>
            {!isPreview && availableSizesButton}
            {isPreview && (
              <StyledIcon
                data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-${availableSizesIcon}-icon`}
                name={availableSizesIcon}
                onClick={() => setState({ openSection: !openSection })}
              />
            )}
          </UserInfoHeadingContainer>
          {openSection && (
            <Row>
              {areCommonSizesAvailable() && (
                <BorderedCol xs="12" sm="4">
                  {!_isEmpty(profile.profileTshirtSizes) &&
                    profile.profileTshirtSizes.map((tshirtSize) => (
                      <SizesContainer key={tshirtSize.id}>
                        <SizeLabel
                          data-qa-id={`${
                            SCREEN_NAMES.PROFILE_VIEW
                          }-${sectionName}-heading-tshirt-${tshirtSize.category.name.toLowerCase()}`}
                        >
                          {t('common:profile.sizes.tShirt')}:
                        </SizeLabel>
                        <SizeLabelValue
                          data-qa-id={`${
                            SCREEN_NAMES.PROFILE_VIEW
                          }-${sectionName}-tshirt-${tshirtSize.category.name.toLowerCase()}`}
                        >
                          {tshirtSize.name} ({tshirtSize.category.name})
                        </SizeLabelValue>
                      </SizesContainer>
                    ))}

                  {!_isEmpty(profile.profileShoeSizes) &&
                    profile.profileShoeSizes.map((shoeSize) => (
                      <SizesContainer key={shoeSize.id}>
                        <SizeLabel
                          data-qa-id={`${
                            SCREEN_NAMES.PROFILE_VIEW
                          }-${sectionName}-heading-shoe-${shoeSize.category.name.toLowerCase()}`}
                        >
                          {t('common:profile.sizes.shoe')}:
                        </SizeLabel>
                        <SizeLabelValue
                          data-qa-id={`${
                            SCREEN_NAMES.PROFILE_VIEW
                          }-${sectionName}-shoe-${shoeSize.category.name.toLowerCase()}`}
                        >
                          {getShoeSize(countryCode, shoeSize)}({shoeSize.category.name}
                          {shoeSize.shoeModifier && `, ${shoeSize.shoeModifier.name}`})
                        </SizeLabelValue>
                      </SizesContainer>
                    ))}

                  {!_isEmpty(profile.profilePants) &&
                    profile.profilePants.map((pants) => (
                      <SizesContainer key={pants.id}>
                        <SizeLabel
                          data-qa-id={`${
                            SCREEN_NAMES.PROFILE_VIEW
                          }-${sectionName}-heading-pants-${pants.gender.name.toLowerCase()}`}
                        >
                          {t('common:profile.sizes.pants')}:
                        </SizeLabel>
                        <SizeLabelValue
                          data-qa-id={`${
                            SCREEN_NAMES.PROFILE_VIEW
                          }-${sectionName}-pants-${pants.gender.name.toLowerCase()}`}
                        >
                          {getSize(countryCode, pants)} ({pants.gender.name})
                        </SizeLabelValue>
                      </SizesContainer>
                    ))}

                  {profile.waist && (
                    <SizesContainer>
                      <SizeLabel data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-waist`}>
                        {waistLabel}:
                      </SizeLabel>
                      <SizeLabelValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-waist`}>
                        {waist}
                      </SizeLabelValue>
                    </SizesContainer>
                  )}

                  {profile.inseam && (
                    <SizesContainer>
                      <SizeLabel data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-inseam`}>
                        {inseamLabel}:
                      </SizeLabel>
                      <SizeLabelValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-inseam`}>
                        {inseam}
                      </SizeLabelValue>
                    </SizesContainer>
                  )}

                  {profile.gloveSize && (
                    <SizesContainer>
                      <SizeLabel data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-glove`}>
                        {t('common:profile.sizes.glove')}:
                      </SizeLabel>
                      <SizeLabelValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-glove`}>
                        {gloveSize}
                      </SizeLabelValue>
                    </SizesContainer>
                  )}

                  {profile.hatSize && (
                    <SizesContainer>
                      <SizeLabel data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-hat`}>
                        {t('common:profile.sizes.hat')}:
                      </SizeLabel>
                      <SizeLabelValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-hat`}>
                        {hatSize}
                      </SizeLabelValue>
                    </SizesContainer>
                  )}
                </BorderedCol>
              )}

              {areWomenSizesAvailable() && (
                <BorderedCol xs="12" sm="4">
                  <SizesSubTitle data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-women-sizes`}>
                    {t('common:profile.sizes.womensSizes')}
                  </SizesSubTitle>
                  {profile.dressSize && (
                    <SizesContainer>
                      <SizeLabel data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-dress`}>
                        {t('common:profile.sizes.dress')}:
                      </SizeLabel>
                      <SizeLabelValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-dress`}>
                        {getSize(countryCode, profile.dressSize)}
                      </SizeLabelValue>
                    </SizesContainer>
                  )}

                  {profile.bust && (
                    <SizesContainer>
                      <SizeLabel data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-bust`}>
                        {t('common:profile.sizes.bust')}:
                      </SizeLabel>
                      <SizeLabelValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-bust`}>
                        {bust}
                      </SizeLabelValue>
                    </SizesContainer>
                  )}

                  {profile.underbust && (
                    <SizesContainer>
                      <SizeLabel data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-underbust`}>
                        {t('common:profile.sizes.underbust')}:
                      </SizeLabel>
                      <SizeLabelValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-underbust`}>
                        {underbust}
                      </SizeLabelValue>
                    </SizesContainer>
                  )}

                  {profile.cupSize && (
                    <SizesContainer>
                      <SizeLabel data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-cup`}>
                        {t('common:profile.sizes.cup')}:
                      </SizeLabel>
                      <SizeLabelValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-cup`}>
                        {profile.cupSize.name}
                      </SizeLabelValue>
                    </SizesContainer>
                  )}

                  {profile.hipSize && (
                    <SizesContainer>
                      <SizeLabel data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-hip`}>
                        {t('common:profile.sizes.hip')}:
                      </SizeLabel>
                      <SizeLabelValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-hip`}>
                        {hipSize}
                      </SizeLabelValue>
                    </SizesContainer>
                  )}
                </BorderedCol>
              )}
              <Col xs="12" sm="4">
                {areMenSizesAvailable() && (
                  <>
                    <SizesSubTitle data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-men-sizes`}>
                      {t('common:profile.sizes.mensSizes')}
                    </SizesSubTitle>
                    {profile.chestSize && (
                      <SizesContainer>
                        <SizeLabel data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-chest`}>
                          {t('common:profile.sizes.chest')}:
                        </SizeLabel>
                        <SizeLabelValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-chest`}>
                          {chestSize}
                        </SizeLabelValue>
                      </SizesContainer>
                    )}

                    {profile.neck && (
                      <SizesContainer>
                        <SizeLabel data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-neck`}>
                          {t('common:profile.sizes.neck')}:
                        </SizeLabel>
                        <SizeLabelValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-neck`}>
                          {neck}
                        </SizeLabelValue>
                      </SizesContainer>
                    )}

                    {profile.sleeve && (
                      <SizesContainer>
                        <SizeLabel data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-sleeve`}>
                          {t('common:profile.sizes.sleeve')}:
                        </SizeLabel>
                        <SizeLabelValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-sleeve`}>
                          {sleeve}
                        </SizeLabelValue>
                      </SizesContainer>
                    )}

                    {profile.coat && (
                      <SizesContainer>
                        <SizeLabel data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-coat`}>
                          {t('common:profile.sizes.coat')}:
                        </SizeLabel>
                        <SizeLabelValue data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-coat`}>
                          {profile.coat.name}
                        </SizeLabelValue>
                      </SizesContainer>
                    )}
                  </>
                )}

                {!_isEmpty(profile.profileSizes) && (
                  <>
                    <SizesSubTitle data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-child-sizes`}>
                      {t('common:profile.sizes.childSizes')}
                    </SizesSubTitle>
                    {profile.profileSizes.map((profileSize) => (
                      <SizesContainer key={profileSize.id}>
                        <SizeLabel
                          data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-${_kebabCase(
                            profileSize.category.name
                          )}`}
                        >
                          {t(`common:profile.sizes.${profileSize.category.name.toLowerCase()}`)}:
                        </SizeLabel>
                        <SizeLabelValue
                          data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-${_kebabCase(
                            profileSize.category.name
                          )}`}
                        >
                          {profileSize.name} {profileSize.sizeModifier && `(${profileSize.sizeModifier.name})`}
                        </SizeLabelValue>
                      </SizesContainer>
                    ))}
                  </>
                )}
              </Col>
            </Row>
          )}
        </UserInfoSection>
      ) : (
        !isPreview && (
          <div>
            <InfoHeading>{t('common:profile.nav.sizes')}</InfoHeading>
            <UserInfoBtnContainer>{commonSizesButton}</UserInfoBtnContainer>
          </div>
        )
      )}
    </UserInfoContainer>
  );
};

export default Sizes;
